import React, { useState } from 'react'

import { SxProps } from '@mui/material'

import { TSourceRow } from 'types/GlobalSource'

import { useQueryParamState } from 'utils/queryHandler'
import { ESourceCategory, SOURCE_CATEGORY_META } from 'utils/sources'

import Box from 'components_new/atoms/Box'

import ChipTabs from 'components_new/molecules/ChipTabs'
import GenericListItem from 'components_new/molecules/GenericListItem'
import SearchField from 'components_new/molecules/SearchField'
import SelectButton from 'components_new/molecules/SelectButton'
import SimpleTable from 'components_new/molecules/SimpleTable'

import { Highlighted } from 'components_new/organisms/StandardSearch/utils'

import { ESourceUsageTab, formatCount, renderUsageCell } from './utils'

interface SourcesTableProps {
  loading?: boolean
  onClickAdd?: () => void
  rows: TSourceRow[]
  sx?: SxProps
}

const SourcesTable = (props: SourcesTableProps) => {
  const { loading, onClickAdd, rows, sx } = props

  /*-- states --*/
  const [searchQuery, setSearchQuery] = useState('')

  const [tab, setTab] = useQueryParamState(
    'tab',
    null,
    Object.values(ESourceUsageTab)
  )

  const [category, setCategory] = useQueryParamState(
    'type',
    null,
    Object.values(ESourceCategory)
  )

  /*-- rows --*/
  const byCategory = rows.filter(
    (row) => !category || row.category === category
  )

  const bySearch = byCategory.filter((row) =>
    row.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Counted before the usage filter is applied, so the chips keep their counts
  // when one of them is selected.
  const usageCounts = {
    [ESourceUsageTab.USED]: bySearch.filter((row) => row.inUse).length,
    [ESourceUsageTab.UNUSED]: bySearch.filter((row) => !row.inUse).length
  }

  const visibleRows = bySearch.filter(
    (row) => tab === null || row.inUse === (tab === ESourceUsageTab.USED)
  )

  return (
    <>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, mb: 1 }}>
        <SearchField
          disabled={loading}
          onClear={() => setSearchQuery('')}
          onSearch={(value) => setSearchQuery(value)}
          placeholder="Sök efter datakälla"
          size="small"
          value={searchQuery}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <ChipTabs
          disabled={loading}
          onChange={(value) => setTab(value as ESourceUsageTab | null)}
          options={[
            {
              count: usageCounts[ESourceUsageTab.USED],
              label: 'Används',
              value: ESourceUsageTab.USED
            },
            {
              count: usageCounts[ESourceUsageTab.UNUSED],
              label: 'Används ej',
              value: ESourceUsageTab.UNUSED
            }
          ]}
          size="small"
          sx={{ flex: 1 }}
          value={tab}
        />

        <SelectButton
          onChange={(value) => setCategory(value as ESourceCategory | null)}
          options={[
            { id: null, title: 'Alla typer' },
            ...Object.values(ESourceCategory).map((value) => ({
              id: value,
              title: SOURCE_CATEGORY_META[value].label
            }))
          ]}
          placeholder="Alla typer"
          showActive={true}
          value={category}
        />
      </Box>

      <SimpleTable
        addRow={onClickAdd ? { onClick: onClickAdd } : undefined}
        columns={[
          { defaultSort: 'asc', title: 'Namn', width: '25%' },
          { title: 'Typ' },
          { title: 'Användning' },
          { align: 'right', title: 'Tabeller' },
          { align: 'right', title: 'Attribut' },
          { align: 'right', title: 'Rader' }
        ]}
        hover={true}
        loading={loading}
        rows={visibleRows.map((row) => {
          const { label } = SOURCE_CATEGORY_META[row.category]

          return {
            cells: [
              <GenericListItem
                customTitle={
                  <Highlighted highlight={searchQuery} text={row.name} />
                }
                disablePadding={true}
                key="name"
                title={row.name}
              />,
              label,
              renderUsageCell(row.usage),
              formatCount(row.tableCount),
              formatCount(row.columnCount),
              formatCount(row.rowCount)
            ],
            href: row.href,
            id: row.id,
            sortValues: [
              { value: row.name },
              { value: label },
              { value: row.usage.length },
              { value: row.tableCount },
              { value: row.columnCount },
              { value: row.rowCount }
            ]
          }
        })}
        size="small"
        sx={sx}
      />
    </>
  )
}

export default SourcesTable
